import { FormEvent } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@@/ui/primitives/button';
import { Label } from '@@/ui/primitives/label';
import { Switch } from '@@/ui/primitives/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@@/ui/primitives/select';
import { Form, FormField } from '@@/ui/primitives/form';
import { StringConstraints } from './constraints/string';
import { NumberConstraints } from './constraints/number';
import { BooleanConstraints } from './constraints/boolean';
import { ReferenceConstraints } from './constraints/reference';
import { PickListConstraints } from './constraints/pick-list';
import clsx from 'clsx';
import { ConnectResourceType } from '../../api/api-entities';
import { Field, FieldSchema, SchemaFieldType } from '../../schemas/entity';
import { JsonConstraints } from './constraints/json';
import { PromptConstraints } from './constraints/prompt';
import { DateTimeConstraints } from './constraints/date-time';
import { ConnectResourceConstraints } from './constraints/connect-resource';
import { FieldContent, FieldError, FieldLabel, Field as UIField } from '@@/ui/primitives/field';
import { YamlConstraints } from './constraints/yaml';
import { ConditionsEditor } from './conditions-editor';
import z from 'zod';
import { SaveIcon, Trash2Icon } from 'lucide-react';
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from '@@/ui/primitives';

export interface PropertyFormProps {
  existingNames: string[];
  currentPrimaryField?: string;
  initialName?: string;
}

/**
 * Creates a dynamic Zod schema for the PropertyEditor form
 * Validates field name, property definition, and metadata
 */
// export function createPropertyFormSchema({ existingNames, currentPrimaryField, initialName }: PropertyFormProps) {
//   const nameSchema = z
//     .string()
//     .min(1, 'Property name is required')
//     .max(32, 'Property name must be between 1 and 32 characters')
//     .refine((value) => !existingNames.includes(value), 'Property name already exists');

//   const formSchema = FieldSchema.extend({
//     name: nameSchema
//   })
//     .refine(
//       (data) => {
//         if (data.primary && data.definition.type !== SchemaFieldType.STRING) {
//           return false;
//         }
//         return true;
//       },
//       {
//         message: 'Only string fields can be set as primary',
//         path: ['primary']
//       }
//     )
//     .refine(
//       (data) => {
//         if (data.primary && !data.definition.required) {
//           return false;
//         }
//         return true;
//       },
//       {
//         message: 'Primary fields must be required',
//         path: ['definition', 'required']
//       }
//     )
//     .refine(
//       (data) => {
//         if (data.primary && currentPrimaryField && currentPrimaryField !== initialName) {
//           return false;
//         }
//         return true;
//       },
//       {
//         message: `Another field "${currentPrimaryField}" is already set as primary`,
//         path: ['primary']
//       }
//     )
//     .refine(
//       (data) => {
//         if (data.conditions && data.conditions.groups.length > 0 && data.primary) {
//           return false;
//         }
//         return true;
//       },
//       {
//         message: 'Conditional fields cannot be set as primary',
//         path: ['primary']
//       }
//     );

//   return formSchema;
// }

// Type inference for the form schema
// export type PropertyFormSchema = ReturnType<typeof createPropertyFormSchema>;
// export type PropertyFormValues = z.infer<typeof FieldSchema, any, z.output<typeof FieldSchema>>;

/**
 * Creates default values for the property form
 */
export function createPropertyFormDefaults(
  propertyType: SchemaFieldType = SchemaFieldType.STRING,
  order: number
): Field {
  const baseDefaults = {
    name: '',
    primary: false,
    overridable: true,
    hidden: false,
    presentable: true,
    access: {},
    inherited: false,
    conditions: {
      groups: []
    },
    order: order
  };

  switch (propertyType) {
    case 'string':
      return {
        ...baseDefaults,
        definition: {
          type: SchemaFieldType.STRING,
          minLength: 1,
          maxLength: 255,
          required: false
        }
      };
    case 'number':
      return {
        ...baseDefaults,
        definition: {
          type: SchemaFieldType.NUMBER,
          minimum: 0,
          maximum: 100,
          required: false
        }
      };
    case 'boolean':
      return {
        ...baseDefaults,
        definition: {
          type: SchemaFieldType.BOOLEAN,
          required: false
        }
      };
    case 'reference':
      return {
        ...baseDefaults,
        definition: {
          type: SchemaFieldType.REFERENCE,
          entityId: '',
          required: false
        }
      };
    case 'picklist':
      return {
        ...baseDefaults,
        definition: {
          type: SchemaFieldType.PICK_LIST,
          options: [],
          multiple: false,
          required: false
        }
      };
    case 'object':
      return {
        ...baseDefaults,
        definition: {
          type: SchemaFieldType.OBJECT,
          required: false
        }
      };

    case 'prompt':
      return {
        ...baseDefaults,
        definition: {
          type: SchemaFieldType.PROMPT,
          required: false
        }
      };

    case 'yaml':
      return {
        ...baseDefaults,
        definition: {
          type: SchemaFieldType.YAML,
          required: false
        }
      };

    case 'date_time':
      return {
        ...baseDefaults,
        definition: {
          type: SchemaFieldType.DATE_TIME,
          required: false
        }
      };

    case 'connect_resource':
      return {
        ...baseDefaults,
        definition: {
          type: SchemaFieldType.CONNECT_RESOURCE,
          resourceType: ConnectResourceType.QUEUE,
          required: false
        }
      };

    default:
      return {
        ...baseDefaults,
        definition: {
          type: SchemaFieldType.STRING,
          minLength: 0,
          maxLength: 255,
          required: false
        }
      };
  }
}

export function PropertyEditor({
  onSave,
  onDelete,
  initialName,
  initialProperty,
  currentPrimaryField,
  allFields,
  hasItems = false,
  hasSubEntities = false
}: {
  onSave: (name: string, field: Field) => void;
  onDelete: (name: string) => void;
  existingNames: string[];
  initialName?: string;
  initialProperty?: Field;
  currentPrimaryField?: string;
  allFields?: Record<string, Field>;
  hasItems: boolean;
  hasSubEntities: boolean;
}) {
  const cannotEditNameOrType = !!initialName && (hasItems || hasSubEntities);
  const isLocked = initialProperty ? initialProperty.inherited : false;
  const canEditOrRenameField = !cannotEditNameOrType && !isLocked;

  const nameForm = useForm({
    resolver: zodResolver(
      z.object({
        name: z.string().min(1).max(32)
      })
    ),
    defaultValues: {
      name: initialName ? initialName : ''
    },
    mode: 'onChange',
    disabled: isLocked
  });

  const defaultValues: Field = initialProperty
    ? initialProperty
    : createPropertyFormDefaults(SchemaFieldType.STRING, Object.keys(allFields || {}).length);
  const form = useForm({
    resolver: zodResolver(FieldSchema),
    defaultValues: defaultValues,
    mode: 'onChange',
    disabled: isLocked
  });

  const definitionType = form.watch('definition.type');
  const isPrimary = form.watch('primary');

  const handleSave = (event: FormEvent) => {
    nameForm.handleSubmit((nameValues) => {
      form.handleSubmit((values) => {
        onSave(nameValues.name, values);
      })(event);
    })(event);
  };

  const handleDelete = () => {
    if (initialName) {
      onDelete(initialName);
    }
  };

  return (
    <FormProvider {...form}>
      <Form {...form}>
        <div className={clsx('flex flex-col overflow-hidden')}>
          <div className='flex'>
            <div className='flex gap-6 flex-col px-4 w-72'>
              <div className='flex flex-col gap-4'>
                <Controller
                  name='name'
                  control={nameForm.control}
                  render={({ field, fieldState }) => (
                    <UIField orientation='responsive' data-invalid={fieldState.invalid} className='gap-2'>
                      <FieldContent>
                        <FieldLabel htmlFor='name'>Name</FieldLabel>
                      </FieldContent>
                      <InputGroup>
                        <InputGroupInput
                          placeholder='Name'
                          id='name'
                          value={field.value}
                          disabled={field.disabled || cannotEditNameOrType}
                          onChange={field.onChange}
                          readOnly={cannotEditNameOrType}
                          className={clsx('text-sm! shadow-none')}
                          aria-invalid={fieldState.invalid}
                        />
                        {cannotEditNameOrType && (
                          <InputGroupAddon align='inline-end'>
                            <InputGroupText className='text-xs!'>Protected</InputGroupText>
                          </InputGroupAddon>
                        )}
                      </InputGroup>
                      {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                      {!initialName && (
                        <div className='text-xs text-neutral-600 px-1 max-w-xs'>
                          Once created, you cannot delete or rename this field unless there are no items or child
                          Entities.
                        </div>
                      )}
                    </UIField>
                  )}
                />
                <Controller
                  name='definition.type'
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <UIField orientation='responsive' data-invalid={fieldState.invalid} className='gap-2'>
                      <FieldContent>
                        <FieldLabel htmlFor='form-rhf-select-type'>Type</FieldLabel>
                        {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                      </FieldContent>
                      <InputGroup>
                        <Select
                          name={field.name}
                          value={field.value}
                          disabled={field.disabled || cannotEditNameOrType}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger
                            id='form-rhf-select-type'
                            aria-invalid={fieldState.invalid}
                            className='text-sm! font-medium! w-full bg-white text-neutral-950 shadow-none'
                          >
                            <SelectValue placeholder='Select' />
                          </SelectTrigger>
                          {/* <InputGroupAddon align='inline-end'>
                            <InputGroupText className='text-xs!'>Protected</InputGroupText>
                          </InputGroupAddon> */}
                          <SelectContent position='item-aligned' className='font-sans text-sm font-medium'>
                            <SelectItem value={SchemaFieldType.STRING}>String</SelectItem>
                            <SelectItem value={SchemaFieldType.NUMBER}>Number</SelectItem>
                            <SelectItem value={SchemaFieldType.BOOLEAN}>Boolean</SelectItem>
                            <SelectItem value={SchemaFieldType.DATE_TIME}>Date Time</SelectItem>
                            <SelectItem value={SchemaFieldType.REFERENCE}>Reference</SelectItem>
                            <SelectItem value={SchemaFieldType.PICK_LIST}>Pick List</SelectItem>
                            <SelectItem value={SchemaFieldType.PROMPT}>Prompt</SelectItem>
                            <SelectItem value={SchemaFieldType.OBJECT}>JSON</SelectItem>
                            <SelectItem value={SchemaFieldType.YAML}>YAML</SelectItem>
                            <SelectItem value={SchemaFieldType.CONNECT_RESOURCE}>Amazon Connect Resource</SelectItem>
                          </SelectContent>
                        </Select>
                      </InputGroup>
                    </UIField>
                  )}
                />
              </div>
              <div className='flex flex-col gap-3 border-t pt-4'>
                <FormField
                  control={form.control}
                  name='definition.required'
                  render={({ field }) => (
                    <div className='flex flex-row items-center justify-between gap-2'>
                      <div className='flex flex-col gap-1.5 text-neutral-950 font-[450]!'>
                        <Label htmlFor='property-required'>Required</Label>
                        <span className='text-muted-foreground text-xs'>
                          Require field to contain a non-empty value
                        </span>
                      </div>
                      <Switch
                        id='property-required'
                        checked={field.value}
                        disabled={field.disabled || form.watch('primary')}
                        onCheckedChange={field.onChange}
                      />
                    </div>
                  )}
                />
                {definitionType === SchemaFieldType.STRING && (
                  <FormField
                    control={form.control}
                    name='primary'
                    render={({ field, fieldState }) => (
                      <div>
                        <div className='flex flex-row items-center justify-between gap-2'>
                          <div className='flex flex-col gap-1.5 text-neutral-950 font-[450]!'>
                            <Label htmlFor='property-primary'>Primary Key</Label>
                            <span className='text-muted-foreground text-xs'>Uniquely identifies an item</span>
                          </div>
                          <Switch
                            id='property-primary'
                            checked={field.value}
                            onCheckedChange={(checked: boolean) => {
                              field.onChange(checked);
                              if (checked) {
                                form.setValue('definition.required', true);
                              }
                            }}
                            disabled={field.disabled || currentPrimaryField == initialName}
                          />
                        </div>
                        {fieldState.error && <p className='text-sm text-red-950 mt-1'>{fieldState.error.message}</p>}
                      </div>
                    )}
                  />
                )}
                <FormField
                  control={form.control}
                  name='overridable'
                  render={({ field }) => (
                    <div className='flex flex-row items-center justify-between gap-2'>
                      <div className='flex flex-col gap-1.5 text-neutral-950 font-[450]!'>
                        <Label htmlFor='property-overridable'>Overridable</Label>
                        <span className='text-muted-foreground text-xs'>Allow defaults of children to be modified</span>
                      </div>
                      <Switch
                        id='property-overridable'
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={field.disabled}
                      />
                    </div>
                  )}
                />
                <FormField
                  control={form.control}
                  name='hidden'
                  render={({ field }) => (
                    <div className='flex flex-row items-center justify-between gap-2'>
                      <div className='flex flex-col gap-1.5 text-neutral-950 font-[450]!'>
                        <Label htmlFor='property-hidden'>Hidden</Label>
                        <span className='text-muted-foreground text-xs'>Adjust visibility in Query Results</span>
                      </div>
                      <Switch
                        id='property-hidden'
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={field.disabled}
                      />
                    </div>
                  )}
                />
                <FormField
                  control={form.control}
                  name='presentable'
                  render={({ field }) => (
                    <div className='flex flex-row items-center justify-between gap-2'>
                      <div className='flex flex-col gap-1.5 text-neutral-950 font-[450]!'>
                        <Label htmlFor='property-presentable'>Presentable</Label>
                        <span className='text-muted-foreground text-xs'>Adjust visibility in the Browser</span>
                      </div>
                      <Switch
                        id='property-presentable'
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={field.disabled}
                      />
                    </div>
                  )}
                />
              </div>
              {definitionType !== 'string' && form.watch('primary') && (
                <div className='p-3 bg-yellow-50 rounded-md'>
                  <p className='text-sm text-yellow-950'>
                    Only string fields can be set as primary. Change the type to string to enable primary.
                  </p>
                </div>
              )}

              {currentPrimaryField && currentPrimaryField !== initialName && form.watch('primary') && (
                <div className='p-3 bg-yellow-50 rounded-md'>
                  <p className='text-sm text-yellow-950'>
                    Another field "{currentPrimaryField}" is already set as primary. Only one field can be primary.
                  </p>
                </div>
              )}

              {initialName && canEditOrRenameField && (
                <Button
                  variant='destructive'
                  onClick={handleDelete}
                  className='text-sm! font-medium! self-end mt-4!'
                  size={'sm'}
                >
                  <Trash2Icon className='size-3.5' />
                  Delete Field
                </Button>
              )}
            </div>
            <div className='flex-1 flex flex-col border-l border rounded-lg mr-2'>
              {definitionType === 'string' && <StringConstraints />}
              {definitionType === 'number' && <NumberConstraints />}
              {definitionType === 'boolean' && <BooleanConstraints />}
              {definitionType === 'reference' && <ReferenceConstraints />}
              {definitionType === 'picklist' && <PickListConstraints />}
              {definitionType === 'object' && <JsonConstraints />}
              {definitionType === 'prompt' && <PromptConstraints />}
              {definitionType === 'date_time' && <DateTimeConstraints />}
              {definitionType === 'yaml' && <YamlConstraints />}
              {definitionType === 'connect_resource' && <ConnectResourceConstraints />}
              {!isPrimary && (
                <div className='border-t p-4'>
                  <ConditionsEditor allFields={allFields} currentFieldName={initialName} isLocked={isLocked} />
                </div>
              )}
            </div>
          </div>
          <div className='flex justify-end gap-4 p-3'>
            <Button onClick={handleSave} className='text-sm! font-medium!' size={'sm'}>
              <SaveIcon className='size-3.5' />
              {initialName ? 'Save' : 'Add'}
            </Button>
          </div>
        </div>
      </Form>
    </FormProvider>
  );
}
